import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import {
  ServiceAppointmentQueryParameters,
  VehicleQueryParameters,
  WorkOrderQueryParameters,
} from '@quorum/models/xs-query';
import { DealerCodeRooftopLink, DealerCodeType, Vehicle, WorkOrder } from '@quorum/models/xs-resource';
import { Appointment } from 'libs/models/xs-resource/fixed-operations/appointment.model';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { GMServiceVisitByDealerQueryParameters, GMServiceVisitByVinQueryParameters } from '@quorum/models/oem-query';

import { ApiService } from '@quorum/api';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { UpdateWorkOrderOnServer } from '@quorum/sha-entities/fixed-operations';
import * as SvcVehicleDiagnosticsRootActions from './svc-vehicle-diagnostics-root.actions';
import { SvcVehicleDiagnosticsRoot } from './svc-vehicle-diagnostics-root.actions';
import { vehicleDiagnosticsQuery } from './svc-vehicle-diagnostics-root.selectors';

import { AuthenticationStateService } from '@quorum/authentication/services';
import { GMServiceVisitByDealer, GMServiceVisitByVin } from '@quorum/models/oem-resource';
import { combineLatest, Observable, of, Subject } from 'rxjs';

@Injectable()
export class SvcVehicleDiagnosticsRootFacade {
  /**
   * Combine pieces of state using createSelector,
   * and expose them as observables through the facade.
   */
  isWithinADay$ = new Subject();

  constructor(
    private readonly store: Store,
    private actions$: Actions,
    public serviceLaneFacade: ServiceLaneFacade,
    private apiService: ApiService,
    private authenticationStateService: AuthenticationStateService
  ) {}

  /**
   * Use the initialization action to perform one
   * or more tasks in your Effects.
   */
  //#region GM
  linkedServiceVisit$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisit));
  serviceVisitsByVin$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectServiceVisitsByVin));
  serviceVisitsByDealer$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectServiceVisitsByDealer));
  linkedServiceVisitDtcs$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisitDtcs));
  linkedServiceVisitMpvi$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisitMpvi));
  linkedServiceVisitEcus$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisitEcus));
  recentServiceVisitBasedOnVin$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectRecentServiceVisitBasedOnVin));

  serviceVisitsByVinStatus$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectServiceVisitByVinStatus));
  serviceVisitsByDealerStatus$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectServiceVisitsByDealerStatus));

  linkedServiceVisitDtcsStatus$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisitDtcsStatus));
  linkedServiceVisitMpviStatus$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisitMpviStatus));
  linkedServiceVisitEcusStatus$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisitEcusStatus));

  serviceVisitsByVinError$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectServiceVisitByVinError));
  serviceVisitsByDealerError$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectServiceVisitsByDealerError));
  linkedServiceVisitDtcsError$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisitDtcsError));
  linkedServiceVisitMpviError$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisitMpviError));
  linkedServiceVisitEcusError$ = this.store.pipe(select(vehicleDiagnosticsQuery.selectLinkedServiceVisitEcusError));

  dealerBac$: Observable<string> = combineLatest([
    this.apiService.get<DealerCodeType[]>(`v/1/system/dealer-code-types`, { params: { code: 'bac' } }).pipe(
      map((dealerCodeTypes: DealerCodeType[]) => dealerCodeTypes[0]?.id),
      filter((id) => !!id)
    ),
    this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      map((employee) => employee?.rooftopId),
      filter((rooftopId) => !!rooftopId),
      take(1)
    ),
  ]).pipe(
    switchMap(([dealerCodeTypeId, rooftopId]) => {
      return this.apiService
        .get<DealerCodeRooftopLink[]>(`/v/1/system/dealer-code-rooftop-links`, {
          params: { dealerCodeTypeId, rooftopId },
        })
        .pipe(map((bacDealerCodeRooftopLinks) => bacDealerCodeRooftopLinks[0]?.dealerCode || ''));
    })
  );

  getAlreadyLinkedServiceVisitIds$: Observable<string[]> = combineLatest([
    this.getServiceAppointmentsWithALinkedServiceVisit(),
    this.getWorkOrdersWithALinkedServiceVisit(),
  ]).pipe(
    map(([linkedServiceAppointments, linkedWorkOrders]) => {
      const linkedServiceVisitIdsFromAppts = linkedServiceAppointments.map(
        (serviceAppointment) => serviceAppointment.serviceVisitId
      );
      const linkedServiceVisitIdsFromWOs = linkedWorkOrders.map((workOrder) => workOrder.serviceVisitId);

      const combinedServiceVisitIds = linkedServiceVisitIdsFromAppts.concat(linkedServiceVisitIdsFromWOs);
      const filterDuplicates = combinedServiceVisitIds.filter((serviceVisitId, index) => {
        return combinedServiceVisitIds.indexOf(serviceVisitId) == index;
      });

      return filterDuplicates;
    })
  );

  gmActionListener(
    businessId: string,
    dealerCountryCode: string,
    vin: string,
    transactionId: any,
    transactionType: string,
    fromXsellerator: boolean
  ) {
    return this.actions$.pipe(
      ofType(
        SvcVehicleDiagnosticsRoot.updateLinkedServiceVisitInState,
        SvcVehicleDiagnosticsRoot.clearLinkedServiceVisitState
      ),
      map((action: any) => {
        switch (action.type) {
          case SvcVehicleDiagnosticsRoot.updateLinkedServiceVisitInState:
            this.getLinkedServiceVisitDtc(businessId, dealerCountryCode, action.serviceVisitId);
            this.getLinkedServiceVisitMpvi(businessId, dealerCountryCode, action.serviceVisitId);
            this.getLinkedServiceVisitEcu(businessId, dealerCountryCode, action.serviceVisitId);

            combineLatest([
              this.getTransaction(transactionId, transactionType, fromXsellerator).pipe(take(1)),
              this.getVehicle(vin, fromXsellerator).pipe(take(1)),
              this.recentServiceVisitBasedOnVin$,
            ])
              .pipe(
                take(1),
                map(([transaction, vehicle, latestServiceVisit]) => {
                  return {
                    transaction: transaction,
                    vehicle: vehicle,
                    latestServiceVisit: latestServiceVisit,
                  };
                })
              )
              .subscribe((combinedData) => {
                if (combinedData.transaction.serviceVisitId !== action.serviceVisitId) {
                  combinedData.transaction.serviceVisitId = action.serviceVisitId;
                  if (
                    !action.preserveAdvisorOdometer &&
                    combinedData?.latestServiceVisit?.serviceVisitId == action?.serviceVisitId &&
                    this.isWithinADay(
                      combinedData.latestServiceVisit.checkInDate,
                      combinedData.latestServiceVisit.checkInTime
                    )
                  ) {
                    combinedData.transaction.odometerIn = combinedData.latestServiceVisit.odometer;
                    combinedData.transaction.odometerUpdatedByVehicleDiagnostic = true;
                    combinedData.vehicle.currentMileage = parseInt(combinedData.latestServiceVisit.odometer);
                  } else {
                    combinedData.transaction.odometerUpdatedByVehicleDiagnostic = false;
                  }

                  if (transactionType === 'appointment') {
                    this.serviceLaneFacade.updateAppointmentOnServer(
                      combinedData.transaction,
                      new ServiceAppointmentQueryParameters({
                        embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
                      })
                    );
                  } else if (transactionType === 'workOrder') {
                    this.store.dispatch(new UpdateWorkOrderOnServer(combinedData.transaction));
                  }

                  if (!action.preserveAdvisorOdometer) {
                    this.serviceLaneFacade.updateVehicleOnServer(combinedData.vehicle);
                  }
                }
              });
            break;
          case SvcVehicleDiagnosticsRoot.clearLinkedServiceVisitState:
            this.getTransaction(transactionId, transactionType, fromXsellerator)
              .pipe(take(1))
              .subscribe((transaction) => {
                transaction.serviceVisitId = null;
                transaction.odometerUpdatedByVehicleDiagnostic = false;
                if (transactionType === 'appointment') {
                  this.serviceLaneFacade.updateAppointmentOnServer(
                    transaction,
                    new ServiceAppointmentQueryParameters({
                      embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
                    })
                  );
                } else if (transactionType === 'workOrder') {
                  this.store.dispatch(new UpdateWorkOrderOnServer(transaction));
                }
              });
            break;
          default:
            break;
        }
      })
    );
  }

  getGMServiceVisitByVin(
    businessId: string,
    dealerCountryCode: string,
    vin: string,
    queryParameters?: GMServiceVisitByVinQueryParameters
  ) {
    this.store.dispatch(
      SvcVehicleDiagnosticsRootActions.getGMServiceVisitByVin({
        businessId: businessId,
        dealerCountryCode: dealerCountryCode,
        vin: vin,
        queryParameters: queryParameters,
      })
    );
  }

  getGMServiceVisitByDealer(
    businessId: string,
    dealerCountryCode: string,
    startDate: string,
    endDate: string,
    bac: string,
    queryParameters?: GMServiceVisitByDealerQueryParameters
  ) {
    this.store.dispatch(
      SvcVehicleDiagnosticsRootActions.getGMServiceVisitByDealer({
        businessId: businessId,
        dealerCountryCode: dealerCountryCode,
        startDate: startDate,
        endDate: endDate,
        bac: bac,
        queryParameters: queryParameters,
      })
    );
  }

  getLinkedServiceVisitDtc(businessId: string, dealerCountryCode: string, serviceVisitId: string) {
    this.store.dispatch(
      SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitDtc({
        businessId: businessId,
        dealerCountryCode: dealerCountryCode,
        serviceVisitId: serviceVisitId,
      })
    );
  }

  getLinkedServiceVisitMpvi(businessId: string, dealerCountryCode: string, serviceVisitId: string) {
    this.store.dispatch(
      SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitMpvi({
        businessId: businessId,
        dealerCountryCode: dealerCountryCode,
        serviceVisitId: serviceVisitId,
      })
    );
  }

  getLinkedServiceVisitEcu(businessId: string, dealerCountryCode: string, serviceVisitId: string) {
    this.store.dispatch(
      SvcVehicleDiagnosticsRootActions.getLinkedServiceVisitEcu({
        businessId: businessId,
        dealerCountryCode: dealerCountryCode,
        serviceVisitId: serviceVisitId,
      })
    );
  }

  updateLinkedServiceVisitInState(serviceVisitId: string, preserveAdvisorOdometer = false) {
    this.store.dispatch(
      SvcVehicleDiagnosticsRootActions.updateLinkedServiceVisitInState({
        serviceVisitId: serviceVisitId,
        preserveAdvisorOdometer,
      })
    );
  }

  clearLinkedServiceVisitState() {
    this.store.dispatch(SvcVehicleDiagnosticsRootActions.clearLinkedServiceVisitState());
  }

  clearVehicleDiagnosticsState() {
    this.store.dispatch(SvcVehicleDiagnosticsRootActions.clearVehicleDiagnosticsState());
  }

  getTransaction(transactionId: number, transactionType: string, fromXsellerator: boolean) {
    if (fromXsellerator) {
      if (transactionType === 'appointment') {
        const appointmentQueryParams: ServiceAppointmentQueryParameters = {
          id: transactionId.toString(),
          embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
        };
        return this.apiService
          .get<Appointment[]>('v/2/fixed-operations/appointments', {
            params: appointmentQueryParams,
          })
          .pipe(
            map((appointments: Appointment[]) => {
              return appointments[0];
            })
          );
      } else if (transactionType === 'workOrder') {
        return this.apiService.get<WorkOrder>(`v/1/fixed-operations/work-orders/${transactionId}`, {}).pipe(
          map((workOrder) => {
            return workOrder;
          })
        );
      } else {
        //Prevent the warning for not all paths return, however one of top two should always hit
        return of(null);
      }
    } else {
      return this.serviceLaneFacade.selectedAppointment$.pipe(
        filter((appointment) => appointment != null),
        take(1),
        map((appointment) => {
          return appointment;
        })
      );
    }
  }

  getVehicle(vin: string, fromXsellerator: boolean) {
    if (fromXsellerator) {
      const vehicleQueryParams: VehicleQueryParameters = {
        vin: vin,
        embed: ['associate', 'type', 'colour'],
      };

      return this.apiService
        .get<Vehicle[]>(`v/1/vehicles/vehicles`, {
          params: vehicleQueryParams,
        })
        .pipe(
          map((vehicles: Vehicle[]) => {
            return vehicles[0];
          })
        );
    } else {
      return this.serviceLaneFacade.vehicle$.pipe(
        filter((vehicle) => vehicle != null),
        take(1),
        map((vehicle) => {
          return vehicle;
        })
      );
    }
  }

  getLatestServiceVisit(serviceVisits: GMServiceVisitByVin[] | GMServiceVisitByDealer[]) {
    return serviceVisits.sort(
      (
        serviceVisitA: GMServiceVisitByVin | GMServiceVisitByDealer,
        serviceVisitB: GMServiceVisitByVin | GMServiceVisitByDealer
      ) => {
        const checkInDateA = this.createCheckInDate(serviceVisitA.checkInDate, serviceVisitA.checkInTime);
        const checkInDateB = this.createCheckInDate(serviceVisitB.checkInDate, serviceVisitB.checkInTime);
        return checkInDateB.getTime() - checkInDateA.getTime();
      }
    )[0];
  }

  isWithinADay(checkInDate: string, checkInTime: string) {
    const now = new Date().getTime();
    const oneDayAgo = new Date().setDate(new Date().getDate() - 1);
    const checkIn = this.createCheckInDate(checkInDate, checkInTime).getTime();
    return checkIn >= oneDayAgo && checkIn <= now;
  }

  getServiceAppointmentsWithALinkedServiceVisit(): Observable<Appointment[]> {
    const appointmentQueryParams: ServiceAppointmentQueryParameters = new ServiceAppointmentQueryParameters({
      hasServiceVisitId: 'true',
    });
    return this.apiService.get<Appointment[]>('v/2/fixed-operations/appointments', {
      params: appointmentQueryParams,
    });
  }

  getWorkOrdersWithALinkedServiceVisit(): Observable<WorkOrder[]> {
    const workOrderQueryParams: WorkOrderQueryParameters = new WorkOrderQueryParameters({
      hasServiceVisitId: 'true',
    });
    return this.apiService.get<WorkOrder[]>('v/2/fixed-operations/work-orders', {
      params: workOrderQueryParams,
    });
  }

  createCheckInDate(gmCheckInDate: string, gmCheckInTime: string) {
    const checkInDate = new Date(
      parseInt(gmCheckInDate.split('-')[0]),
      parseInt(gmCheckInDate.split('-')[1]) - 1,
      parseInt(gmCheckInDate.split('-')[2]),
      parseInt(gmCheckInTime.split(':')[0]),
      parseInt(gmCheckInTime.split(':')[1]),
      parseInt(gmCheckInTime.split(':')[2]),
      parseInt(gmCheckInTime.split('.')[1])
    );
    return checkInDate;
  }

  //#endregion

  init() {
    this.store.dispatch(SvcVehicleDiagnosticsRootActions.init());
  }
}
